/**
 * Facet helper functions for building and transforming Voyager facet data.
 * Extracted from VoyagerSearchService.ts.
 */

import type {
  VoyagerFacetConfig,
  VoyagerFacetValueCategoryConfig,
} from './VoyagerSearchService.types';
import { parseOptionalNumber } from '../../../../utils/parseUtils';

export const stripFacetLocalParams = (fieldName: string): string =>
  fieldName.replace(/\{!ex=[^}]+\}/g, '').trim();

export const buildFacetDisplayNames = (
  facetConfig: VoyagerFacetConfig,
): Record<string, string> =>
  facetConfig.fields.reduce(
    (displayNames, facetField) => {
      const normalizedField = stripFacetLocalParams(facetField.field);
      if (normalizedField) {
        displayNames[normalizedField] = facetField.label || normalizedField;
      }
      return displayNames;
    },
    {} as Record<string, string>,
  );

export const buildFacetMaxValues = (
  facetConfig: VoyagerFacetConfig,
): Record<string, number> =>
  facetConfig.fields.reduce(
    (maxValues, facetField) => {
      const normalizedField = stripFacetLocalParams(facetField.field);
      const maxValue = parseOptionalNumber(facetField.maxValues);
      if (normalizedField && maxValue !== undefined) {
        maxValues[normalizedField] = maxValue;
      }
      return maxValues;
    },
    {} as Record<string, number>,
  );

export const buildFacetCategoryPaths = (
  facetConfig: VoyagerFacetConfig,
): Record<string, string[]> =>
  facetConfig.fields.reduce(
    (categoryPaths, facetField) => {
      const normalizedField = stripFacetLocalParams(facetField.field);
      if (normalizedField && facetField.categoryPath?.length) {
        categoryPaths[normalizedField] = facetField.categoryPath;
      }
      return categoryPaths;
    },
    {} as Record<string, string[]>,
  );

export const buildFacetShortLabelFlags = (
  facetConfig: VoyagerFacetConfig,
): Record<string, boolean> =>
  facetConfig.fields.reduce(
    (shortLabelFlags, facetField) => {
      const normalizedField = stripFacetLocalParams(facetField.field);
      if (normalizedField && facetField.useShortLabels) {
        shortLabelFlags[normalizedField] = true;
      }
      return shortLabelFlags;
    },
    {} as Record<string, boolean>,
  );

export const buildFacetValueCategories = (
  facetConfig: VoyagerFacetConfig,
): Record<string, VoyagerFacetValueCategoryConfig[]> =>
  facetConfig.fields.reduce(
    (valueCategories, facetField) => {
      const normalizedField = stripFacetLocalParams(facetField.field);
      if (normalizedField && facetField.valueCategories?.length) {
        valueCategories[normalizedField] = facetField.valueCategories;
      }
      return valueCategories;
    },
    {} as Record<string, VoyagerFacetValueCategoryConfig[]>,
  );

export const buildFacetParams = (
  facetConfig: VoyagerFacetConfig,
): Record<string, string> => {
  const params = {
    ...(facetConfig.params || {}),
  };

  facetConfig.fields.forEach((facetField) => {
    const normalizedField = stripFacetLocalParams(facetField.field);
    const maxValue = parseOptionalNumber(facetField.maxValues);
    if (
      normalizedField &&
      maxValue !== undefined &&
      !Object.prototype.hasOwnProperty.call(
        params,
        `f.${normalizedField}.facet.limit`,
      )
    ) {
      params[`f.${normalizedField}.facet.limit`] = String(maxValue);
    }
  });

  return params;
};

export const getConfiguredFacetFieldOrder = (
  facetConfig: VoyagerFacetConfig,
): string[] =>
  facetConfig.fields.map((facetField) =>
    stripFacetLocalParams(facetField.field),
  );

/**
 * Determine which facet sections declare a Solr exclude tag for their own
 * field (for example `{!ex=grp_NSDI}grp_NSDI`). The `fq` filters for these
 * sections must be tagged with a matching `{!tag=...}` so that selecting a
 * value does not collapse the section's sibling values, while still allowing
 * the active keyword search and other filters to narrow the counts.
 */
export const buildFacetExcludeTagSections = (
  facetConfig: VoyagerFacetConfig,
): Set<string> => {
  const excludeSections = new Set<string>();

  facetConfig.fields.forEach((facetField) => {
    const normalizedField = stripFacetLocalParams(facetField.field);
    if (!normalizedField) {
      return;
    }

    const excludeMatches = facetField.field.match(/\{!ex=([^}]+)\}/g) || [];
    const declaresSelfExclude = excludeMatches.some((match) => {
      const tagList = match.replace(/\{!ex=|\}/g, '');
      return tagList
        .split(',')
        .map((tag) => tag.trim())
        .includes(normalizedField);
    });

    if (declaresSelfExclude) {
      excludeSections.add(normalizedField);
    }
  });

  return excludeSections;
};

export const shortenFacetValueLabel = (value: string): string => {
  if (!value.includes('/')) {
    return value;
  }

  let shortValue = value.split('/').pop() || value;

  if (shortValue.startsWith('vnd.')) {
    shortValue = shortValue.substring(4);
  }

  if (shortValue.startsWith('x-')) {
    shortValue = shortValue.substring(2);
  }

  return shortValue || value;
};
